import { Injectable } from '@angular/core';
import {
  DataRouteParamModuleIndex,
  DataRouteParamWorkshopId,
  DataWorkshopEntity,
  DataWorkshopEntityId,
} from '@edgvr-front/data/domain';
import {
  createEffect,
  Actions,
  ofType,
  EffectNotification,
  concatLatestFrom,
} from '@ngrx/effects';
import { getSelectors } from '@ngrx/router-store';
import { Store } from '@ngrx/store';
import { Observable } from 'rxjs';
import {
  distinctUntilChanged,
  exhaustMap,
  filter,
  map,
  mergeMap,
  switchMap,
  takeUntil,
} from 'rxjs/operators';
import { DataWorkshopModulesListPageActions } from '../actions';
import { DataWorkshopGetRequestDTO } from '../dto';
import { DataWorkshopsSelectors } from '../selectors';
import { DataService } from '../services';

const { selectRouteParams } = getSelectors();

@Injectable()
export class DataWorkshopModulesListPageEffects {
  dataWorkshopGet$ = createEffect(() =>
    this.actions$.pipe(
      ofType(DataWorkshopModulesListPageActions.dataWorkshopGet),
      switchMap((_) =>
        this.dataService
          .dataWorkshopGet(new DataWorkshopGetRequestDTO({ id: _.id }))
          .pipe(
            mergeMap((_) => [
              DataWorkshopModulesListPageActions.dataWorkshopGetSuccess({
                dataWorkshop: DataWorkshopEntity.factory(_.data.dataWorkshop),
              }),
            ])
          )
      )
    )
  );

  selectedWorkshopSet$ = createEffect(() =>
    this.store.select(selectRouteParams).pipe(
      map((_) => Number(_[DataRouteParamWorkshopId]) as DataWorkshopEntityId),
      filter((_) => Number.isSafeInteger(_)),
      distinctUntilChanged(),
      concatLatestFrom((_) => [
        this.store.select(DataWorkshopsSelectors.dataWorkshop(_)),
        this.store.select(DataWorkshopsSelectors.selectedDataWorkshop),
      ]),
      filter(
        ([workshopId, _dataWorkshop, selectedDatWorkshop]) =>
          selectedDatWorkshop?.value.id !== workshopId
      ),
      mergeMap(([workshopId, dataWorkshop]) =>
        dataWorkshop
          ? [
              DataWorkshopModulesListPageActions.selectedDataWorkshopSet({
                dataWorkshop,
              }),
            ]
          : [
              DataWorkshopModulesListPageActions.dataWorkshopGet({
                id: workshopId,
              }),
            ]
      )
    )
  );

  selectedModuleSet$ = createEffect(() =>
    this.store.select(DataWorkshopsSelectors.selectedDataWorkshop).pipe(
      // 'selectedDataModuleSet' action may produce infinite loop
      distinctUntilChanged((lhs, rhs) => lhs?.value.id === rhs?.value.id),
      switchMap((selectedDataWorkshop) =>
        selectedDataWorkshop
          ? this.store.select(selectRouteParams).pipe(
              map((_) => Number(_[DataRouteParamModuleIndex])),
              distinctUntilChanged(),
              mergeMap((_) => {
                const dataModule = selectedDataWorkshop?.value.data.modules[_];

                return dataModule
                  ? [
                      DataWorkshopModulesListPageActions.selectedDataModuleSet({
                        dataModule,
                      }),
                    ]
                  : [];
              })
            )
          : []
      )
    )
  );

  constructor(
    private readonly actions$: Actions,
    private readonly store: Store,
    private readonly dataService: DataService
  ) {}

  ngrxOnRunEffects(resolvedEffects$: Observable<EffectNotification>) {
    return this.actions$.pipe(
      ofType(DataWorkshopModulesListPageActions.init),
      exhaustMap(() =>
        resolvedEffects$.pipe(
          takeUntil(
            this.actions$.pipe(
              ofType(DataWorkshopModulesListPageActions.destroy)
            )
          )
        )
      )
    );
  }
}
